import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { COARSE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import type { FeedCategoryRead, FeedSourceRead } from '../../types/api';
import { FeedSourceRow } from './FeedSourceRow';
import { sourceLabel } from './feedSourceLabels';
import type { FeedCategoryCollection, FeedSourceCollection } from './newsApi';
import { useDragAutoScroll } from './useDragAutoScroll';

/**
 * Drag payload type. A private MIME rather than `text/plain` alone, so a card
 * can tell a source row from a paragraph of text dragged in off another page —
 * the same rule the calendar's chips follow. `text/plain` is set as well
 * because some platforms refuse a drag that carries nothing they recognise.
 */
export const FEED_SOURCE_DRAG_TYPE = 'application/x-outpost-feed-source';

/** Server-side cap on a category name. Mirrors `FeedCategoryCreate`. */
export const FEED_CATEGORY_NAME_MAX = 80;

/** What a category is called before the user has said. Born in rename mode. */
const NEW_CATEGORY_NAME = 'New category';

/**
 * Drop-target key for the fixed bucket at the bottom. Not an id: the bucket is
 * the absence of a category, so it has none.
 */
const UNCATEGORISED = 'uncategorised';

/**
 * Who gets into the bucket: only what a Describe run went and found.
 *
 * The bucket is a to-do list, not a census. A source the user pasted or ticked
 * off our list was a deliberate choice and is already listed, unfiled, under
 * **Add your own**; repeating it here would make every board open on a wall of
 * rows nobody intends to file. What a run turned up is the opposite — it
 * arrived without anyone picking it, so it is exactly what wants sorting.
 *
 * Only the *unfiled* side is filtered. A source already inside a category is
 * drawn whatever its provenance: the user put it there, and a board that hid a
 * row it was told to hold would be lying about where things are.
 */
function belongsInBucket(source: FeedSourceRead): boolean {
  return source.discovered_via === 'agent';
}

/**
 * A request to bring one category into view and mark it.
 *
 * An object rather than a bare id so that asking for the same category twice —
 * two curation receipts in a row filing into it — is two distinct requests, and
 * the second one scrolls again instead of being swallowed as "no change".
 */
export interface FeedCategoryFocus {
  categoryId: string;
}

/** How long the arrived-here mark stays on a card handed over from another tab. */
const FOCUS_MARK_MS = 4000;

/** The panel's own wording for a category that has left the list underneath it. */
function categoryErrorMessage(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError && caught.code === 'RESOURCE_NOT_FOUND') {
    return 'That category is no longer there. Close and reopen this dialog.';
  }
  return getErrorMessage(caught, fallback);
}

/** "1 source" / "3 sources", the only arithmetic a card does. */
function sourceCount(count: number): string {
  return count === 1 ? '1 source' : `${count} sources`;
}

interface NewsCategoriesPanelProps {
  /**
   * The one categories collection `/news` holds, owned by the page like the
   * sources one — the sources list under the headlines tags its rows out of the
   * same collection, so a rename here reaches it the same render.
   */
  categories: FeedCategoryCollection;
  /**
   * The dialog's one sources collection. A move is a PATCH on a row of this
   * list, so filing a source here files it everywhere it is shown.
   */
  sources: FeedSourceCollection;
  /** Set when another tab handed over: scroll to that card and mark it. */
  focus: FeedCategoryFocus | null;
}

/**
 * Your categories — the user's own folders over the sources they follow.
 *
 * A board rather than a form: cards down one column, sources filed inside them,
 * and a fixed bucket at the bottom for everything not filed yet. A source is
 * moved by dragging its row onto a card (native HTML5 drag, as the calendar's
 * chips move between days) or by the "Move to" select on the row, which is the
 * keyboard and touch route to exactly the same PATCH.
 *
 * The bucket at the bottom is not everything unfiled — it is what a Describe
 * run found and nobody has filed yet. A pasted or catalogue source is already
 * listed under Add your own and would only be noise here. See
 * {@link belongsInBucket}.
 *
 * A drag scrolls the dialog for itself (`useDragAutoScroll`), because the
 * platform will not: without it a board of five cards can be dragged at but
 * never dragged past.
 *
 * Both moves are optimistic: the row is drawn in its new card straight away and
 * goes back where it was if the call fails, with the reason said above the
 * board. Nothing here changes what the newsfeed *reads* — a category is filing,
 * not following — so nothing here re-pulls the headlines, and deleting a
 * category leaves every source in it followed, in the bucket at the bottom.
 *
 * No model call, no run, no quota. Categories are named by the curation runs
 * that create them and renamed by hand; that naming is the only place a model
 * is ever involved, and it happened on another tab.
 */
export function NewsCategoriesPanel({ categories, sources, focus }: NewsCategoriesPanelProps) {
  // Optimistic overlays. Both hold the value that has been sent but not yet
  // confirmed, and both are dropped the moment the server row catches up.
  const [pendingMoves, setPendingMoves] = useState<Record<string, string | null>>({});
  const [pendingNames, setPendingNames] = useState<Record<string, string>>({});

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  const [dropKey, setDropKey] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<FeedCategoryRead | null>(null);
  // A finger cannot drag a row between cards (no touch DnD here), so on a
  // touch screen the lede names the route it does have.
  const isCoarse = useMediaQuery(COARSE_QUERY);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The focus request whose mark has timed out. Held rather than a "marked id",
  // so the mark is derived during render and the effect below writes no state
  // synchronously — the only setState it makes is inside the timer.
  const [fadedFocus, setFadedFocus] = useState<FeedCategoryFocus | null>(null);

  const cardRefs = useRef<Record<string, HTMLElement | null>>({});
  const nameInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The board is taller than the dialog and a native drag scrolls nothing on
  // its own, so a row picked up at the bottom could never reach a card at the
  // top. Held for the length of the gesture: started on pick-up, stopped on
  // every way the gesture can end.
  const autoScroll = useDragAutoScroll(panelRef);

  /** A card born in rename mode, or a name clicked: put the caret in the field. */
  useEffect(() => {
    if (editingId === null) return;
    nameInputRef.current?.select();
  }, [editingId]);

  /**
   * Handed over from the curation receipt: bring the card into view and mark it
   * for a moment. The mark times out because it answers a question already
   * asked — it is a "here it is", not a state of the category.
   */
  useEffect(() => {
    if (focus === null) return;
    cardRefs.current[focus.categoryId]?.scrollIntoView({ block: 'center' });
    const timer = window.setTimeout(() => setFadedFocus(focus), FOCUS_MARK_MS);
    return () => window.clearTimeout(timer);
  }, [focus]);

  const markedId = focus !== null && focus !== fadedFocus ? focus.categoryId : null;

  /** Where a source is filed right now: the overlay when one is in flight. */
  function categoryIdOf(source: FeedSourceRead): string | null {
    const pending = pendingMoves[source.id];
    return pending === undefined ? source.category_id : pending;
  }

  function nameOf(category: FeedCategoryRead): string {
    return pendingNames[category.id] ?? category.name;
  }

  /** Drops an overlay entry, but only if it is still the one that was sent. */
  function settleMove(sourceId: string, sent: string | null) {
    setPendingMoves((current) => {
      if (!(sourceId in current) || current[sourceId] !== sent) return current;
      const next: Record<string, string | null> = {};
      for (const [key, value] of Object.entries(current)) {
        if (key !== sourceId) next[key] = value;
      }
      return next;
    });
  }

  function settleName(categoryId: string, sent: string) {
    setPendingNames((current) => {
      if (current[categoryId] !== sent) return current;
      const next: Record<string, string> = {};
      for (const [key, value] of Object.entries(current)) {
        if (key !== categoryId) next[key] = value;
      }
      return next;
    });
  }

  /** The move, from a drop or from the select. Onto its own card it is nothing. */
  function moveSource(sourceId: string, categoryId: string | null) {
    const source = sources.items.find((item) => item.id === sourceId);
    if (!source || categoryIdOf(source) === categoryId) return;

    setWriteError(null);
    setPendingMoves((current) => ({ ...current, [sourceId]: categoryId }));

    void sources
      .update(sourceId, { category_id: categoryId })
      .then(() => settleMove(sourceId, categoryId))
      .catch((caught: unknown) => {
        // Back where it came from, and say why rather than leave it moved.
        settleMove(sourceId, categoryId);
        setWriteError(categoryErrorMessage(caught, 'That source could not be moved.'));
      });
  }

  async function createCategory() {
    setIsCreating(true);
    setWriteError(null);
    try {
      const created = await categories.create({ name: NEW_CATEGORY_NAME });
      // Straight into rename: the placeholder name is not an answer, it is a
      // prompt, and the field is already open when the card appears.
      setDraftName(created.name);
      setEditingId(created.id);
    } catch (caught) {
      setWriteError(categoryErrorMessage(caught, 'That category could not be created.'));
    } finally {
      setIsCreating(false);
    }
  }

  function startRename(category: FeedCategoryRead) {
    setWriteError(null);
    setDraftName(nameOf(category));
    setEditingId(category.id);
  }

  /** Enter, or the Save button. An emptied field reverts rather than clears. */
  function commitRename(category: FeedCategoryRead) {
    const trimmed = draftName.trim();
    setEditingId(null);
    if (trimmed.length === 0 || trimmed === nameOf(category)) return;

    setWriteError(null);
    setPendingNames((current) => ({ ...current, [category.id]: trimmed }));

    void categories
      .update(category.id, { name: trimmed })
      .then(() => settleName(category.id, trimmed))
      .catch((caught: unknown) => {
        settleName(category.id, trimmed);
        setWriteError(categoryErrorMessage(caught, 'That category could not be renamed.'));
      });
  }

  /** Escape abandons the edit: the field is a control, not a form to cancel. */
  function handleNameKeys(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Escape') return;
    // Closing is the default action of Escape inside a native `<dialog>`, so
    // this has to be prevented outright: the key is abandoning a rename here,
    // not asking the whole Edit feed dialog to shut.
    event.preventDefault();
    event.stopPropagation();
    setEditingId(null);
  }

  function confirmDelete(category: FeedCategoryRead) {
    setIsDeleting(true);
    setDeleteError(null);
    void categories
      .remove(category.id)
      .then(() => {
        // The server left every source in it followed with `category_id` null.
        // Saying so locally is exactly what happened, and cheaper than a refetch.
        setPendingMoves((current) => {
          const next = { ...current };
          for (const source of sources.items) {
            const pending = current[source.id];
            const held = pending === undefined ? source.category_id : pending;
            if (held === category.id) next[source.id] = null;
          }
          return next;
        });
        if (editingId === category.id) setEditingId(null);
        setDeleting(null);
      })
      .catch((caught: unknown) => {
        setDeleteError(categoryErrorMessage(caught, 'That category could not be deleted.'));
      })
      .finally(() => setIsDeleting(false));
  }

  // Ordered by the server's own `sort_order`, not by arrival: `create` puts a
  // new row at the front of the collection the way every newest-first list
  // wants, and this is the one list that is ordered instead.
  const ordered = [...categories.items].sort(
    (a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at),
  );

  const filed = new Map<string, FeedSourceRead[]>();
  for (const source of sources.items) {
    const categoryId = categoryIdOf(source);
    if (categoryId === null && !belongsInBucket(source)) continue;
    const key = categoryId ?? UNCATEGORISED;
    const bucket = filed.get(key);
    if (bucket) bucket.push(source);
    else filed.set(key, [source]);
  }

  /** Every card down the column, with the fixed bucket last. */
  const cards: readonly { key: string; category: FeedCategoryRead | null }[] = [
    ...ordered.map((category) => ({ key: category.id, category })),
    { key: UNCATEGORISED, category: null },
  ];

  /** The drop side of the move, shared by the cards and the bucket. */
  function dropProps(key: string, categoryId: string | null) {
    return {
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (!event.dataTransfer.types.includes(FEED_SOURCE_DRAG_TYPE)) return;
        // Preventing the default is what makes this a drop target at all.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (dropKey !== key) setDropKey(key);
      },
      onDragLeave: (event: DragEvent<HTMLElement>) => {
        // Crossing between the card's own children fires a leave on the card
        // as well; only a pointer that has really left it drops the highlight.
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) return;
        setDropKey((current) => (current === key ? null : current));
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        setDropKey(null);
        autoScroll.stop();
        const sourceId = event.dataTransfer.getData(FEED_SOURCE_DRAG_TYPE);
        if (sourceId) moveSource(sourceId, categoryId);
      },
    };
  }

  return (
    <div
      className={
        draggingId === null ? 'feed-categories' : 'feed-categories feed-categories--dragging'
      }
      ref={panelRef}
    >
      <div className="feed-categories__head">
        <p className="field__hint feed-categories__lede">
          {isCoarse
            ? 'Folders over the sources you already follow. Filing one changes nothing about what is read. Use Move to on any row.'
            : 'Folders over the sources you already follow. Filing one changes nothing about what is read — drop a source anywhere on a category card, or use Move to. Drag near the top or bottom and the list scrolls itself.'}
        </p>
        <button
          className="button"
          type="button"
          disabled={isCreating || categories.isLoading}
          onClick={() => void createCategory()}
        >
          {isCreating ? 'Adding' : 'New category'}
        </button>
      </div>

      {categories.error ? (
        <p className="form__error" role="alert">
          {categories.error}
        </p>
      ) : null}

      {categories.deleteError ? (
        <p className="form__error" role="alert">
          {categories.deleteError}
        </p>
      ) : null}

      {writeError ? (
        <p className="form__error" role="alert">
          {writeError}
        </p>
      ) : null}

      {categories.isLoading || sources.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your categories
        </p>
      ) : (
        <ul className="feed-categories__list">
          {cards.map(({ key, category }) => {
            const items = filed.get(key) ?? [];
            const isEditing = category !== null && editingId === category.id;
            const title = category === null ? 'Not in a category' : nameOf(category);

            const classes = ['feed-category'];
            if (category === null) classes.push('feed-category--loose');
            if (dropKey === key) classes.push('feed-category--drop');
            if (category !== null && markedId === category.id) {
              classes.push('feed-category--marked');
            }

            return (
              <li
                className={classes.join(' ')}
                key={key}
                aria-label={`${title}, ${sourceCount(items.length)}`}
                ref={(node) => {
                  cardRefs.current[key] = node;
                }}
                {...dropProps(key, category === null ? null : category.id)}
              >
                <div className="feed-category__head">
                  {isEditing && category !== null ? (
                    <form
                      className="feed-category__rename"
                      onSubmit={(event: FormEvent<HTMLFormElement>) => {
                        event.preventDefault();
                        commitRename(category);
                      }}
                      noValidate
                    >
                      <label className="visually-hidden" htmlFor={`feed-category-${category.id}`}>
                        Category name
                      </label>
                      <input
                        className="field__input feed-category__input"
                        id={`feed-category-${category.id}`}
                        type="text"
                        maxLength={FEED_CATEGORY_NAME_MAX}
                        value={draftName}
                        ref={nameInputRef}
                        onChange={(event) => setDraftName(event.target.value)}
                        onKeyDown={handleNameKeys}
                      />
                      <button className="button button--ghost" type="submit">
                        Save
                      </button>
                      <button
                        className="button button--ghost"
                        type="button"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                      <span className="feed-category__hint">Empty keeps the old name.</span>
                    </form>
                  ) : (
                    <>
                      {category === null ? (
                        <h3 className="feed-category__name">{title}</h3>
                      ) : (
                        <>
                          <h3 className="feed-category__name">
                            <button
                              className="feed-category__name-button"
                              type="button"
                              onClick={() => startRename(category)}
                            >
                              {title}
                            </button>
                          </h3>
                          <button
                            className="feed-category__pencil"
                            type="button"
                            aria-label={`Rename ${title}`}
                            onClick={() => startRename(category)}
                          >
                            <span aria-hidden="true">✎</span>
                          </button>
                        </>
                      )}
                      <span className="feed-category__count">{sourceCount(items.length)}</span>
                      {category !== null ? (
                        <button
                          className="button button--ghost button--danger-text feed-category__delete"
                          type="button"
                          onClick={() => {
                            setDeleteError(null);
                            setDeleting(category);
                          }}
                        >
                          Delete
                        </button>
                      ) : (
                        // Said where the absence would otherwise read as a bug:
                        // this bucket holds what a run went and found, and
                        // nothing else is missing from the board.
                        <p className="feed-category__note">
                          Sources from our list and your own pastes live under Add your own.
                        </p>
                      )}
                    </>
                  )}
                </div>

                {items.length === 0 ? (
                  <p className="feed-category__empty">
                    {category === null
                      ? 'Nothing waiting to be filed.'
                      : 'Nothing filed here yet. Drag a source in, or pick this category under Move to.'}
                  </p>
                ) : (
                  <ul className="feed-following__list feed-category__sources">
                    {items.map((source) => (
                      <FeedSourceRow
                        key={source.id}
                        source={source}
                        badge="status"
                        drag={{
                          isDragging: draggingId === source.id,
                          onDragStart: (event) => {
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData(FEED_SOURCE_DRAG_TYPE, source.id);
                            event.dataTransfer.setData('text/plain', sourceLabel(source));
                            setDraggingId(source.id);
                            autoScroll.start();
                          },
                          // Fires however the drag ended — dropped, cancelled
                          // with Escape, or let go over nothing.
                          onDragEnd: () => {
                            setDraggingId(null);
                            autoScroll.stop();
                          },
                        }}
                        action={
                          <label className="feed-move">
                            <span className="visually-hidden">
                              Move {sourceLabel(source)} to a category
                            </span>
                            <select
                              className="field__input feed-move__select"
                              value={categoryIdOf(source) ?? ''}
                              onChange={(event) =>
                                moveSource(source.id, event.target.value || null)
                              }
                            >
                              <option value="">No category</option>
                              {ordered.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {nameOf(option)}
                                </option>
                              ))}
                            </select>
                          </label>
                        }
                      />
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {deleting ? (
        <ConfirmDialog
          title={`Delete ${nameOf(deleting)}?`}
          message="The sources stay followed. They move out of this category, and the newsfeed keeps reading them exactly as before."
          confirmLabel="Delete category"
          busyLabel="Deleting"
          isBusy={isDeleting}
          error={deleteError}
          onConfirm={() => confirmDelete(deleting)}
          onDismiss={() => {
            setDeleteError(null);
            setDeleting(null);
          }}
        />
      ) : null}
    </div>
  );
}
